/**
 * Regra de negócio: um database Pipefy por “raiz de área” para filtros gestor.
 * Em geral a raiz é um centro Nível 1 (`solis_centros_custo.nivel`).
 *
 * Exceção: **Jurídico, Compliance e Risco** (N1 único) não gera um database;
 * em vez disso, **Jurídico** e **Compliance** (N2) cada um gera o seu database.
 */
export const PIPEFY_GLOBAL_AREAS_TABLE_ID = "306546622" as const;
/** Mesmo database de Áreas/Subáreas — `connectedRepoId` em createField (ver SubtiposDespesaTab). */
export const PIPEFY_GLOBAL_AREAS_CONNECTED_REPO_ID = "bvoPUmtK" as const;

/** UUID do centro N1 “Jurídico, Compliance e Risco” — não gera DB único; ver SPLIT. */
export const N1_JURIDICO_COMPLIANCE_RISCO_ID = "8d1b20d4-e726-4c81-a3a8-eef74e208897";

/**
 * N1 que são substituídos por uma ou mais raízes N2 (cada uma = 1 database).
 * Ordem: importa para expansão da lista de databases na etapa “área + subáreas”.
 */
export const N1_DATABASE_SPLIT_TO_N2: Record<
  string,
  { label: string; n2CentroIds: readonly string[] }
> = {
  [N1_JURIDICO_COMPLIANCE_RISCO_ID]: {
    label: "Jurídico, Compliance e Risco → 2 databases (Jurídico N2 + Compliance N2)",
    n2CentroIds: [
      "008a78c9-85d3-4ebf-ac9e-dbe163f69fc4", // Jurídico (Nível 2)
      "f4e9e458-8077-4e3f-8fa1-b7701a1d4531", // Compliance (Nível 2)
    ],
  },
};

export type AreaDatabaseRoot = {
  /** Centro que define o database (N1 normalmente, ou N2 na exceção) */
  centroId: string;
  nome: string;
  nivel: string | null;
  /** "n1" | "n2_split" */
  origem: "n1" | "n2_split";
  /** Se veio de split, N1 de origem */
  substituiN1Id?: string;
};

/**
 * A partir dos centros ativos (id, nome, nivel), devolve as raízes que devem ter 1 database cada.
 * @param centros linhas de solis_centros_custo ativas com id, nome, nivel
 */
export function expandRootsForAreaDatabases(
  centros: Array<{ id: string; nome: string; nivel: string | null }>
): AreaDatabaseRoot[] {
  const byId = new Map(centros.map((c) => [c.id, c]));

  const n1List = centros.filter((c) => {
    const n = String(c.nivel || "").trim();
    return n.includes("Nível 1") || n.includes("Nivel 1");
  });

  const out: AreaDatabaseRoot[] = [];

  for (const n1 of n1List) {
    const split = N1_DATABASE_SPLIT_TO_N2[n1.id];
    if (split) {
      for (const n2Id of split.n2CentroIds) {
        const row = byId.get(n2Id);
        if (row) {
          out.push({
            centroId: row.id,
            nome: row.nome,
            nivel: row.nivel,
            origem: "n2_split",
            substituiN1Id: n1.id,
          });
        }
      }
      continue;
    }
    out.push({
      centroId: n1.id,
      nome: n1.nome,
      nivel: n1.nivel,
      origem: "n1",
    });
  }

  return out.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

/** Linha mínima de `solis_centros_custo` para calcular membros do escopo por raiz. */
export type CentroEscopoRow = {
  id: string;
  nome: string;
  nivel: string | null;
  diretoria_nome: string | null;
  area_pai_nome: string | null;
  pipefy_database_id: string | null;
};

export type AreaEscopoPapel = "area" | "subarea";

export type AreaEscopoMember = {
  centroId: string;
  nome: string;
  nivel: string | null;
  papel: AreaEscopoPapel;
};

function normKey(v: string | null | undefined) {
  if (!v) return "";
  return v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

export function isNivel1(nivel: string | null | undefined) {
  const n = String(nivel || "").trim();
  return n.includes("Nível 1") || n.includes("Nivel 1");
}

export function isNivel2(nivel: string | null | undefined) {
  const n = String(nivel || "").trim();
  return n.includes("Nível 2") || n.includes("Nivel 2");
}

export function isNivel3(nivel: string | null | undefined) {
  const n = String(nivel || "").trim();
  return n.includes("Nível 3") || n.includes("Nivel 3");
}

/**
 * Membros do database Pipefy de estrutura por área: a raiz (Área) + filhos na hierarquia (Subárea).
 * - Raiz N1: mesma diretoria (`diretoria_nome` = nome do N1), excluindo a própria raiz.
 * - Raiz N2 (split Jurídico/Compliance): N3 com `area_pai_nome` = nome do N2.
 */
export function membersForAreaRoot(
  root: AreaDatabaseRoot,
  centros: CentroEscopoRow[]
): AreaEscopoMember[] {
  const byId = new Map(centros.map((c) => [c.id, c]));
  const rootRow = byId.get(root.centroId);
  if (!rootRow) return [];

  const out: AreaEscopoMember[] = [];

  if (root.origem === "n2_split") {
    out.push({
      centroId: rootRow.id,
      nome: rootRow.nome,
      nivel: rootRow.nivel,
      papel: "area",
    });
    const rootNome = String(rootRow.nome || "").trim();
    const rootKey = normKey(rootNome);
    for (const c of centros) {
      if (c.id === rootRow.id) continue;
      if (!isNivel3(c.nivel)) continue;
      const pai = normKey(String(c.area_pai_nome || "").trim());
      if (pai && pai === rootKey) {
        out.push({
          centroId: c.id,
          nome: c.nome,
          nivel: c.nivel,
          papel: "subarea",
        });
      }
    }
    return out.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }

  out.push({
    centroId: rootRow.id,
    nome: rootRow.nome,
    nivel: rootRow.nivel,
    papel: "area",
  });

  const dirKey = normKey(String(rootRow.nome || "").trim());
  if (!dirKey) return out;

  for (const c of centros) {
    if (c.id === rootRow.id) continue;
    if (!isNivel2(c.nivel) && !isNivel3(c.nivel)) continue;
    const d = normKey(String(c.diretoria_nome || "").trim());
    if (d && d === dirKey) {
      out.push({
        centroId: c.id,
        nome: c.nome,
        nivel: c.nivel,
        papel: "subarea",
      });
    }
  }

  return out.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}
